<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import NewMessageTextarea from "@/views/chats/components/messages/NewMessageTextarea.vue";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { API } from "@/api/api";
import { useChatStore } from "@/store/chat.store";
import { useFile } from "@/composables/useFile";
import { useMessageDraft } from "@/composables/useMessageDraft";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import type { Response } from "@/api/client";
import type { MessageDto } from "@/api/chat/dto/message.dto";
import type { ChatStateThread } from "@/store/chat.store";

const props = defineProps<{
  thread: ChatStateThread;
}>();

const emit = defineEmits<{
  (e: "aftersent"): void;
}>();

defineOptions({ name: "NewMessageManager" });

const { t } = useI18n();
const chatStore = useChatStore();
const { dataURLtoFile } = useFile();
const { draft, saveDraft, removeDraft } = useMessageDraft();
const api = new API();

const text = ref("");
const file = ref<File | null>(null);
const dragover = ref(false);
const loading = ref(false);

function restoreDraft() {
  const draftMessage = draft(props.thread);

  text.value = draftMessage.message ?? "";
  file.value = draftMessage.file ? dataURLtoFile(draftMessage.file.data, draftMessage.file.name) : null;
}

function saveAsDraft() {
  const currentFile = file.value;

  if (!currentFile) {
    saveDraft(props.thread.id, text.value, null);

    return;
  }

  const reader = new FileReader();
  reader.readAsDataURL(currentFile);
  reader.onload = () => {
    saveDraft(props.thread.id, text.value, { name: currentFile.name, data: String(reader.result) });
  };
}

function processResponse(threadId: number, resp: Response<MessageDto>) {
  if (!resp.data) {
    return;
  }

  chatStore.updateThreadMessage({ ...resp.data, threadId: threadId });
}

function sendText(threadId: number, message: string) {
  return api.chat.sendMessage(threadId, message);
}

function sendFile(threadId: number, attachment: File) {
  const formData = new FormData();
  formData.append("file", attachment);

  return api.chat.sendFile(threadId, formData);
}

function setFile(newFile: File | null) {
  file.value = newFile;
  saveAsDraft();
}

function removeFile() {
  file.value = null;
  saveAsDraft();
}

async function sendMessages() {
  const message = text.value.trim();

  if (loading.value || (message.length < 1 && file.value === null)) {
    return;
  }

  loading.value = true;
  const threadId = props.thread.id;

  try {
    if (message.length > 0) {
      const response = await sendText(threadId, message);
      processResponse(threadId, response);
      text.value = "";
    }

    if (file.value !== null) {
      const response = await sendFile(threadId, file.value);
      processResponse(threadId, response);
      removeFile();
    }

    removeDraft(threadId);
    emit("aftersent");
  } catch (err) {
    console.error("Error add a new message", err);
  } finally {
    loading.value = false;
  }
}

function onFileDrop(event: DragEvent, disabled: boolean) {
  if (disabled) {
    return;
  }

  dragover.value = false;
  const dropped = event.dataTransfer?.files;

  if (!dropped || dropped.length === 0) {
    return;
  }

  setFile(dropped[0]);
}

watch(
  () => props.thread,
  () => {
    restoreDraft();
  },
);

onMounted(() => {
  restoreDraft();
});
</script>
<template>
  <div v-if="!thread.user.archived">
    <permission-tooltip :permission="DispatcherPermissionValue.SendMessage">
      <template #activator="{ disabled }">
        <div
          :class="{ dragover: dragover }"
          class="pt-3 pb-4 px-4 new-message d-flex align-center"
          @dragenter.prevent="dragover = true"
          @dragleave.prevent="dragover = false"
          @dragover.prevent
          @drop.prevent="onFileDrop($event, disabled)"
        >
          <div v-if="dragover && !disabled" class="file-dropzone text-center rounded flex-1 py-5">
            <v-icon>mdi-upload</v-icon>
            <div class="text-center font-size-12">Drop file here to send</div>
          </div>
          <template v-else>
            <div class="d-flex flex-1 flex-column">
              <new-message-textarea
                v-model="text"
                :disabled="!thread.id || disabled"
                :file="file"
                :loading="loading"
                @change="saveAsDraft"
                @submit="sendMessages"
                @update:file="setFile"
              />
            </div>
          </template>
        </div>
      </template>
      {{ t("permission.send-messages") }}
    </permission-tooltip>
  </div>
  <div v-else class="border rounded mx-4 my-3 pa-3 d-flex align-center justify-center">
    <v-icon color="grey-darken-1" icon="mdi-alert-circle-outline" size="20"></v-icon>
    <div class="text-grey-darken-1 font-size-16 ml-2">Sending messages to archived users is not allowed</div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.file-dropzone {
  border: 2px dashed $primary;
  height: 96px;
  background-color: $grey-300;
}

.dragover {
  & * {
    pointer-events: none;
  }
}
</style>
